import React, { useEffect, useId, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { LntDrawer } from '@app/components/base'
import { ExternalProductsStatusTagRender } from '@app/components/renderers'
import { CommonExternalSearchCatalogDrawer } from '@app/drawers/CommonExternalSearchCatalogDrawer'

import { useNotification, useToggle } from '@app/hooks'

import { getExternalProductsListItemDataFromResponse } from '@app/utils/responseAdapters'
import { isEmptyString } from '@app/utils'

import { EXTERNAL_PRODUCTS_CATALOG_KEY } from '@app/constants/queryKeys'
import { EXTERNAL_PRODUCT_STATUS } from '@app/pages/ExternalProductsCatalogPage/сonstans'

import {
  getExternalProductsListItem,
  patchExternalProductsListItem,
  patchExternalProductsProductLink,
  postExternalProductsLink
} from '@app/api/routes/externalProductsCatalog'

import { ProductCardDrawerFooter } from './ProductCardDrawerFooter'
import { ProductCardForm } from './ProductCardForm'

import type { ProductCardDrawerProps } from './types'
import type { ExternalProductsListItemFormData } from './ProductCardForm/types'
import type { PostExternalProductsLinkData } from '@app/api/routes/externalProductsCatalog/types'

export const ProductCardDrawer: React.FC<ProductCardDrawerProps> = props => {
  const { id, onClose, ...drawerProps } = props

  const [currentId, setCurrentId] = useState(id ?? '')

  const productLentaDrawerToggle = useToggle()
  const productConfirmedDrawerToggle = useToggle()
  const productNotConfirmedDrawerToggle = useToggle()

  const formId = useId()
  const queryClient = useQueryClient()
  const notification = useNotification()

  const { data: productsListItemData } = useQuery({
    enabled: !isEmptyString(currentId),
    queryKey: [EXTERNAL_PRODUCTS_CATALOG_KEY.externalProductsListItem, currentId],
    queryFn: async () => await getExternalProductsListItem(currentId)
  })

  const patchProductsListItemDataMutation = useMutation({
    mutationFn: patchExternalProductsListItem,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [EXTERNAL_PRODUCTS_CATALOG_KEY.externalProductsListItem] })
      await queryClient.invalidateQueries({ queryKey: [EXTERNAL_PRODUCTS_CATALOG_KEY.externalProductsList] })

      notification.success({
        message: 'Успешно сохранено'
      })
    }
  })

  const patchExternalProductsLentaLinkDataMutation = useMutation({
    mutationFn: patchExternalProductsProductLink,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [EXTERNAL_PRODUCTS_CATALOG_KEY.externalProductsListItem] })
      await queryClient.invalidateQueries({ queryKey: [EXTERNAL_PRODUCTS_CATALOG_KEY.externalProductsList] })

      notification.success({
        message: 'Объединение выполнено',
        description: 'Успешное объединение товаров'
      })
    }
  })

  const createExternalProductsLinkMutation = useMutation({
    mutationFn: postExternalProductsLink,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [EXTERNAL_PRODUCTS_CATALOG_KEY.externalProductsListItem] })
      await queryClient.invalidateQueries({ queryKey: [EXTERNAL_PRODUCTS_CATALOG_KEY.externalProductsList] })

      notification.success({
        message: 'Объединение выполнено',
        description: 'Успешное объединение товаров'
      })
    }
  })

  const productsListItemDataSource = useMemo(
    () => getExternalProductsListItemDataFromResponse(productsListItemData),
    [productsListItemData]
  )

  const productStatus = useMemo(
    () => productsListItemDataSource?.status ?? EXTERNAL_PRODUCT_STATUS.notConfirmed,
    [productsListItemDataSource]
  )

  const handleSubmit = async (outerData: ExternalProductsListItemFormData): Promise<void> => {
    const isNotConfirmed = productStatus === EXTERNAL_PRODUCT_STATUS.notConfirmed

    const data = {
      ...outerData,
      id: currentId
    }

    if (isNotConfirmed) data.isConfirmed = true

    await patchProductsListItemDataMutation.mutateAsync({ ...data, id: currentId })

    onClose?.()
  }

  const handleLentaProductsLinkAdd = async (selectedIds?: string[]): Promise<void> => {
    if (!selectedIds) return

    const selectedId = selectedIds[0]

    patchExternalProductsLentaLinkDataMutation.mutate({ id: currentId, productId: selectedId })

    await queryClient.invalidateQueries({ queryKey: [EXTERNAL_PRODUCTS_CATALOG_KEY.externalProductsListItem] })
    await queryClient.invalidateQueries({ queryKey: [EXTERNAL_PRODUCTS_CATALOG_KEY.dependentProductsList] })

    productLentaDrawerToggle.setUntoggled()
  }

  const handleProductsConfirmedLinkAdd = async (selectedIds?: string[]): Promise<void> => {
    if (!selectedIds) return

    const selectedId = selectedIds[0]

    const data: PostExternalProductsLinkData = {
      generalExternalProductId: selectedId,
      dependentExternalProductIds: [currentId]
    }

    createExternalProductsLinkMutation.mutate(data)
    setCurrentId(selectedId)

    await queryClient.invalidateQueries({ queryKey: [EXTERNAL_PRODUCTS_CATALOG_KEY.externalProductsListItem] })
    await queryClient.invalidateQueries({ queryKey: [EXTERNAL_PRODUCTS_CATALOG_KEY.dependentProductsList] })
  }

  const handleProductsNotConfirmedLinkAdd = async (selectedIds?: string[]): Promise<void> => {
    if (!selectedIds) return

    const data: PostExternalProductsLinkData = {
      generalExternalProductId: currentId,
      dependentExternalProductIds: selectedIds
    }

    createExternalProductsLinkMutation.mutate(data)

    await queryClient.invalidateQueries({ queryKey: [EXTERNAL_PRODUCTS_CATALOG_KEY.externalProductsListItem] })
    await queryClient.invalidateQueries({ queryKey: [EXTERNAL_PRODUCTS_CATALOG_KEY.dependentProductsList] })

    productNotConfirmedDrawerToggle.setUntoggled()
  }

  useEffect(() => setCurrentId(id ?? ''), [id])

  return (
    <LntDrawer
      { ...drawerProps }
      width={ 672 }
      onClose={ onClose }
      title={
        <ExternalProductsStatusTagRender
          title={ productsListItemDataSource?.name }
          status={ productStatus }
        />
      }
      footer={
        <ProductCardDrawerFooter
          formId={ formId }
          status={ productStatus }
          onClose={ onClose }
          onShowConfirmedDrawer={ productConfirmedDrawerToggle.setToggled }
        />
      }
    >
      <ProductCardForm
        formId={ formId }
        id={ currentId }
        dataSource={ productsListItemDataSource }
        onSubmit={ handleSubmit }
        onShowProductLentaDrawer={ productLentaDrawerToggle.setToggled }
        onShowNotConfirmedDrawer={ productNotConfirmedDrawerToggle.setToggled }
      />

      <CommonExternalSearchCatalogDrawer
        open={ productLentaDrawerToggle.value }
        title='Список товаров Ленты'
        onClose={ productLentaDrawerToggle.setUntoggled }
        onAddProduct={ handleLentaProductsLinkAdd }
        isSearchProducts
      />

      <CommonExternalSearchCatalogDrawer
        open={ productConfirmedDrawerToggle.value }
        title='Список подтвержденных внешних товаров'
        onClose={ productConfirmedDrawerToggle.setUntoggled }
        onAddProduct={ handleProductsConfirmedLinkAdd }
        isConfirmed
      />

      <CommonExternalSearchCatalogDrawer
        open={ productNotConfirmedDrawerToggle.value }
        title='Список неподтвержденных внешних товаров'
        onClose={ productNotConfirmedDrawerToggle.setUntoggled }
        onAddProduct={ handleProductsNotConfirmedLinkAdd }
      />
    </LntDrawer>
  )
}
